/* eslint-disable global-require */
import React, { KeyboardEvent, useContext, useEffect, useRef, useState } from 'react'
import { ButtonModel } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// assets
import ImageIcon from '../../../../assets/icons/ImageIcon'

// types
import { Image as ImageType } from '../../../../types/types'

// styles
import * as SC from './GalleryDesktopStyles'
import { CONTAINER_X_PADDING, MAX_WIDTHS } from '../../../../styles/constants'

// hooks
import useMessages from '../../../../hooks/useMessages'

// utils
import { AppContext } from '../../../../utils/appProvider'
import { checkWebPSupport } from '../../../../utils/helper'
import Modal, { ModalModel } from '../../../../atoms/Modal/Modal'

// constants
const MAX_PREVIEW_IMAGES_COUNT = 5
const GALLERY_TABPANEL_ID = 'salon-gallery-tabpanel'
const getGalleryTabId = (imageId: ImageType['id']) => `salon-gallery-tab-${imageId}`

type SalonGalleryProps = {
	images: ImageType[]
}

const GalleryDesktop = (props: SalonGalleryProps) => {
	const { images } = props

	const { assetsPath } = useContext(AppContext)
	const { messages } = useMessages()
	const { formatMessage } = useIntl()

	const [isLoading, setIsLoading] = useState(true)
	const [photoIndex, setPhotoIndex] = useState<number>(-1)
	const modalOpen = photoIndex > -1
	const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([])

	const placeholderSmall = `${assetsPath}/${require('../../../../assets/images/basic-salon-placeholder-small.jpg')}`
	const placeholderSmallWebP = `${assetsPath}/${require('../../../../assets/images/basic-salon-placeholder-small.webp')}`
	const selectedImage = images[photoIndex]
	const selectedTabId = selectedImage ? getGalleryTabId(selectedImage.id) : undefined

	const selectPhotoByIndex = (next: number) => {
		setPhotoIndex(next)
		requestAnimationFrame(() => {
			thumbnailRefs.current[next]?.focus()
		})
	}

	const handleThumbnailKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
			return
		}

		event.preventDefault()

		const next = event.key === 'ArrowLeft' ? Math.max(photoIndex - 1, 0) : Math.min(photoIndex + 1, images.length - 1)

		selectPhotoByIndex(next)
	}

	useEffect(() => {
		const checkImageLoaded = () => {
			const img = new Image()
			img.src = checkWebPSupport() && placeholderSmallWebP ? placeholderSmallWebP : placeholderSmall
			img.onload = () => setIsLoading(false)
			img.onerror = () => setIsLoading(false)
		}

		checkImageLoaded()
	}, [placeholderSmall, placeholderSmallWebP])

	const gridImages: React.ReactNode[] = []

	if (images.length === 0) {
		gridImages.push(
			<div key={'placeholder-image'}>
				<SC.GalleryImagePicture>
					<source srcSet={`${assetsPath}/${require('../../../../assets/images/basic-salon-placeholder.webp')}`} type='image/webp' />
					<SC.GalleryImage $clickable={false} src={`${assetsPath}/${require('../../../../assets/images/basic-salon-placeholder.jpg')}`} alt={''} />
				</SC.GalleryImagePicture>
			</div>
		)
	} else {
		const containerWidth = MAX_WIDTHS.PAGE_DEFAULT - 2 * CONTAINER_X_PADDING
		const previewImages = images.slice(0, MAX_PREVIEW_IMAGES_COUNT)
		const totalCount = images.length

		previewImages.forEach((image, index) => {
			gridImages.push(
				<SC.PreviewButton
					key={image.id}
					type='button'
					aria-haspopup='dialog'
					aria-label={formatMessage(
						{ id: 'Open gallery, photo {index} of {total}', defaultMessage: 'Open gallery, photo {index} of {total}' },
						{ index: index + 1, total: totalCount }
					)}
					onClick={() => setPhotoIndex(index)}
				>
					<SC.GalleryImage
						$clickable={true}
						loading={index === 0 ? 'eager' : 'lazy'}
						src={index === 0 ? image.resizedImages.large : image.resizedImages.medium}
						alt={''}
						srcSet={
							index === 0
								? `
							${image.resizedImages.medium} 1080w,
							${image.resizedImages.large} 1500w
            			`
								: `
							${image.resizedImages.small} 480w,
							${image.resizedImages.medium} 1080w,
            			`
						}
						sizes={
							index === 0
								? `(max-width: ${containerWidth}}px) 50vw, ${containerWidth / 2}px`
								: `(max-width: ${containerWidth}px) 25vw, ${containerWidth / 4}px`
						}
					/>
				</SC.PreviewButton>
			)
		})
	}

	const loadingImages = gridImages.map((_, index) => (
		<div key={index}>
			<SC.GalleryImagePicture>
				<source srcSet={placeholderSmallWebP} type='image/webp' />
				<SC.GalleryImage src={placeholderSmall} $clickable={false} alt={''} loading={'eager'} />
			</SC.GalleryImagePicture>
		</div>
	))

	return (
		<>
			{/* grid */}
			<SC.ImageGridWrapper>
				{(() => {
					if (isLoading) {
						return <SC.ImageGrid $numberOfImages={loadingImages.length}>{loadingImages}</SC.ImageGrid>
					}

					return (
						<>
							{/* loading images */}
							<SC.ImageGrid $numberOfImages={gridImages.length}>{gridImages}</SC.ImageGrid>

							{images.length > 1 && (
								<SC.LightboxOpenButton
									buttonSize={ButtonModel.Sizes.small}
									buttonStyle={ButtonModel.Styles.primary}
									aria-haspopup='dialog'
									onClick={() => setPhotoIndex(0)}
								>
									<ImageIcon />
									<span>{messages['Show all images']}</span>
								</SC.LightboxOpenButton>
							)}
						</>
					)
				})()}
			</SC.ImageGridWrapper>

			{modalOpen && (
				<SC.ModalWrapper>
					<Modal
						show={modalOpen}
						// NOTE: when color is different than black, it shows hydration error for some reason:
						// hook.js:608 Warning: Prop `color` did not match. Server: "#000000" Client: "#dc0069" Error Component Stack
						closeIconColor={'icon.primary'}
						onClose={() => setPhotoIndex(-1)}
						size={ModalModel.ModalSizes.s1200}
						ariaLabel={messages['Salon gallery']}
					>
						<SC.MainImageWrapper role='tabpanel' id={GALLERY_TABPANEL_ID} aria-labelledby={selectedTabId}>
							<SC.ModalImage src={selectedImage?.resizedImages.large || ''} alt={''} />
						</SC.MainImageWrapper>
						<SC.ThumbnailButtonsContainer role='tablist' aria-label={messages['Salon gallery']}>
							{images.map((image, index) => {
								const isSelected = index === photoIndex
								const tabId = getGalleryTabId(image.id)

								return (
									<SC.ThumbnailButton
										key={image.id}
										ref={(node) => {
											thumbnailRefs.current[index] = node
										}}
										id={tabId}
										type='button'
										role='tab'
										tabIndex={isSelected ? 0 : -1}
										aria-selected={isSelected}
										aria-controls={GALLERY_TABPANEL_ID}
										aria-label={formatMessage({ id: 'Photo {index}' }, { index: index + 1 })}
										onFocus={() => setPhotoIndex(index)}
										onClick={() => setPhotoIndex(index)}
										onKeyDown={handleThumbnailKeyDown}
									>
										<SC.ModalImage src={image.resizedImages.thumbnail || ''} alt={''} />
									</SC.ThumbnailButton>
								)
							})}
						</SC.ThumbnailButtonsContainer>
					</Modal>
				</SC.ModalWrapper>
			)}
		</>
	)
}

export default GalleryDesktop
